'use client'

import React, { useEffect, useRef, useState } from 'react'

// ── Word-by-word "materialize" narration ─────────────────────────────────────────────────────
// Splits copy into per-word spans and NARRATES it: each word's reveal DURATION scales with its
// length and its start time advances by character count, so the soft sweep travels at a steady
// speed through the words. `phrases` (instead of `text`) inserts a `pause` "mood beat" between
// clauses so the reader comprehends each before the next. Timing is computed here and handed to CSS
// as per-word --delay / --dur custom props. `emphasize` lists phrase indices to render heavier.
export function MaterializeWords({
  text,
  phrases,
  base = 0,
  cps = 0.05, // seconds per character (sweep speed)
  pause = 0.7, // seconds held between phrases
  stretch = 2, // each word's reveal lasts this × its time-slot, so neighbours overlap & blend.
  // Multiplicative (not a fixed add-on) keeps the edge at a CONSTANT speed across long/short words.
  emphasize = [],
}: {
  text?: string
  phrases?: string[]
  base?: number
  cps?: number
  pause?: number
  stretch?: number
  emphasize?: number[]
}) {
  const chunks = phrases ?? [text ?? '']
  const flat: { word: string; chunk: number }[] = []
  chunks.forEach((chunk, ci) => chunk.split(' ').forEach((word) => flat.push({ word, chunk: ci })))

  let t = base
  return (
    <>
      {flat.map((item, i) => {
        const dur = Math.max(item.word.length * cps * stretch, 0.4) // floor so 1–2 char words don't pop
        const delay = t
        t += (item.word.length + 1) * cps // advance past the word + its trailing space
        const isChunkEnd = i === flat.length - 1 || flat[i + 1].chunk !== item.chunk
        if (isChunkEnd && item.chunk < chunks.length - 1) t += pause // mood beat between phrases
        const isLast = i === flat.length - 1
        const em = emphasize.includes(item.chunk)
        return (
          <React.Fragment key={i}>
            <span
              className={em ? 'word word--em' : 'word'}
              style={{ '--delay': `${delay.toFixed(3)}s`, '--dur': `${dur.toFixed(3)}s` } as React.CSSProperties}
            >
              {item.word}
            </span>
            {isLast ? '' : ' '}
          </React.Fragment>
        )
      })}
    </>
  )
}

// ── Scroll-triggered reveal ──────────────────────────────────────────────────────────────────
// Fades/rises a block in the first time it enters the viewport (then stays). `stagger` instead
// reveals the element's direct children one after another. This is what gives the scrollable pages
// their "content gracefully arrives as you move through it" native-app feel.
export function Reveal({
  children,
  className = '',
  as: Tag = 'div',
  stagger = false,
  delay = 0,
  style,
}: {
  children: React.ReactNode
  className?: string
  as?: React.ElementType
  stagger?: boolean
  delay?: number
  style?: React.CSSProperties
}) {
  const ref = useRef<HTMLElement>(null)
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    // If it's already in view on mount (e.g. above the fold), reveal next frame.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true)
            io.disconnect()
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  const cls = ['reveal', stagger ? 'reveal--stagger' : '', shown ? 'is-shown' : '', className]
    .filter(Boolean)
    .join(' ')
  return (
    <Tag ref={ref} className={cls} style={{ '--reveal-delay': `${delay}s`, ...style } as React.CSSProperties}>
      {children}
    </Tag>
  )
}

// ── Narrated deck panel ──────────────────────────────────────────────────────────────────────
// A content slide that NARRATES when it becomes active (not on mount): eyebrow rolls, heading writes
// itself word-by-word, lead writes with phrase beats, then the `children` (cards / CTA) fade up.
// Same read→comprehend→continue cadence as the hero. A tap anywhere while it's narrating skips to
// the end (and that tap's click is swallowed so it can't hit a control the user hasn't seen).
export function NarratedPanel({
  active,
  eyebrow,
  headline,
  leadPhrases,
  emphasizeLead,
  children,
}: {
  active: boolean
  eyebrow: string
  headline: string
  leadPhrases: string[]
  emphasizeLead?: number[]
  children: React.ReactNode
}) {
  const [play, setPlay] = useState(false)
  const [done, setDone] = useState(false)

  const EYEBROW_END = 1.4
  const BEAT = 0.7
  const hCps = 0.05
  const hBase = EYEBROW_END + BEAT
  const hEnd = hBase + headline.length * hCps + 0.5
  const leadBase = hEnd + BEAT
  const lCps = 0.028
  const lPause = 0.5
  const leadText = leadPhrases.join(' ')
  const leadEnd = leadBase + leadText.length * lCps + (leadPhrases.length - 1) * lPause + 0.5
  const afterDelay = leadEnd + BEAT
  const totalMs = (afterDelay + 1.1) * 1000

  useEffect(() => {
    if (!active) return
    setPlay(true)
    let finished = false
    const finish = (skipTap: boolean) => {
      if (finished) return
      finished = true
      setDone(true)
      window.removeEventListener('pointerdown', onPointerDown)
      if (skipTap) {
        let t: ReturnType<typeof setTimeout>
        const swallow = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          window.removeEventListener('click', swallow, true)
          clearTimeout(t)
        }
        window.addEventListener('click', swallow, true)
        t = setTimeout(() => window.removeEventListener('click', swallow, true), 700)
      }
    }
    // Require two taps within ~3s to skip — a single tap is too easy to trigger by accident.
    let firstTapAt = 0
    const onPointerDown = () => {
      const now = Date.now()
      if (now - firstTapAt <= 3000) finish(true)
      else firstTapAt = now
    }
    const timer = setTimeout(() => finish(false), totalMs)
    window.addEventListener('pointerdown', onPointerDown)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('pointerdown', onPointerDown)
    }
  }, [active, totalMs])

  return (
    <div
      className={`mh-intro ${play ? 'play' : 'prep'} ${done ? 'done' : ''}`}
      style={{ '--actions-delay': `${afterDelay.toFixed(2)}s` } as React.CSSProperties}
    >
      <div className="section-head section-head--center">
        <span className="eyebrow">{eyebrow}</span>
        <h2>
          <MaterializeWords text={headline} base={hBase} cps={hCps} />
        </h2>
        <p className="lead">
          <MaterializeWords
            phrases={leadPhrases}
            base={leadBase}
            cps={lCps}
            pause={lPause}
            emphasize={emphasizeLead}
          />
        </p>
      </div>
      <div className="mh-after">{children}</div>
    </div>
  )
}

// ── Media-hero narration (owner / renter home heroes) ────────────────────────────────────────
// Plays on mount: eyebrow rolls in, the headline writes itself word-by-word, then the lead and the
// actions fade up. Lead/action delays are derived from the headline length so it stays well-paced
// for any copy. Lead is a quick fade (not a slow word-sweep) so the action buttons aren't far away.
export function MediaHeroIntro({
  eyebrow,
  headline,
  leadPhrases,
  emphasizeLead,
  actions,
  contained = false,
}: {
  eyebrow: string
  headline: string
  leadPhrases: string[] // narrated clause-by-clause with mood beats
  emphasizeLead?: number[] // lead phrase indices to render heavier
  actions?: React.ReactNode
  // On full-screen decks the skip listens on window (tap anywhere). On a scrollable CONTENT page,
  // set contained so the tap-to-skip only listens on the hero itself — otherwise it would swallow
  // the user's first click anywhere on the page (a link, a button below the fold).
  contained?: boolean
}) {
  const [play, setPlay] = useState(false)
  const [done, setDone] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // Beat-paced narration (read → comprehend → continue): each element waits for the previous to
  // finish plus a mood beat — nothing overlaps. Eyebrow rolls 0.3s→1.4s (1.1s, set in CSS).
  const EYEBROW_END = 1.4
  const BEAT = 0.7
  const hCps = 0.05
  const hBase = EYEBROW_END + BEAT // headline starts after the eyebrow lands + a beat
  const hEnd = hBase + headline.length * hCps + 0.5 // ≈ last word's reveal finishing
  const leadBase = hEnd + BEAT // lead begins writing after the headline lands + a beat
  const lCps = 0.028
  const lPause = 0.5
  const leadText = leadPhrases.join(' ')
  const leadEnd =
    leadBase + leadText.length * lCps + (leadPhrases.length - 1) * lPause + 0.5
  const actionsDelay = leadEnd + BEAT // actions after the lead is fully narrated + a beat
  const totalMs = (actionsDelay + 1.1) * 1000

  useEffect(() => {
    const raf = requestAnimationFrame(() => setPlay(true))
    // Skip listens on the hero element when contained (content page), else window (full-screen deck).
    const skipTarget: HTMLElement | Window = contained ? (rootRef.current ?? window) : window
    let finished = false
    // Finish the intro once — either naturally (timer) or because the user tapped to skip.
    const finish = (skipTap: boolean) => {
      if (finished) return
      finished = true
      setDone(true)
      skipTarget.removeEventListener('pointerdown', onPointerDown)
      // When a TAP triggered the skip, swallow that same tap's click so it can't activate a button
      // the user hadn't seen yet (pointerdown → [reveal] → click would otherwise hit the live link).
      if (skipTap) {
        let t: ReturnType<typeof setTimeout>
        const swallow = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          window.removeEventListener('click', swallow, true)
          clearTimeout(t)
        }
        window.addEventListener('click', swallow, true)
        t = setTimeout(() => window.removeEventListener('click', swallow, true), 700)
      }
    }
    // Require two taps within ~3s to skip — a single tap is too easy to trigger by accident.
    let firstTapAt = 0
    const onPointerDown = () => {
      const now = Date.now()
      if (now - firstTapAt <= 3000) finish(true)
      else firstTapAt = now
    }
    const timer = setTimeout(() => finish(false), totalMs)
    skipTarget.addEventListener('pointerdown', onPointerDown)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(timer)
      skipTarget.removeEventListener('pointerdown', onPointerDown)
    }
  }, [totalMs, contained])

  return (
    <div
      ref={rootRef}
      className={`mh-intro ${play ? 'play' : 'prep'} ${done ? 'done' : ''}`}
      style={{ '--actions-delay': `${actionsDelay.toFixed(2)}s` } as React.CSSProperties}
    >
      <span className="eyebrow">{eyebrow}</span>
      <h1>
        <MaterializeWords text={headline} base={hBase} cps={hCps} />
      </h1>
      <p className="lead">
        <MaterializeWords
          phrases={leadPhrases}
          base={leadBase}
          cps={lCps}
          pause={lPause}
          emphasize={emphasizeLead}
        />
      </p>
      {actions ? <div className="hero-actions">{actions}</div> : null}
      <div className="hero-powered">
        Powered by <strong>B.C. Strata Tech</strong>
      </div>
    </div>
  )
}
